/**
 * Per-device push preferences — the pure half.
 *
 * SPLIT OUT OF push.ts SO IT CAN BE TESTED. These three functions carry the
 * empty-means-everything invariant, and a regression in them is silent: draw an
 * unconfigured device as seven unticked boxes and the operator's natural
 * correction (tick them all) collapses straight back to empty. Nothing errors,
 * the phone keeps working, and the screen has been lying since.
 *
 * push.ts imports `./api`, and the test program resolves modules as node16 —
 * where that extensionless import is an error. Keeping this file free of
 * relative imports is what lets tests/unit/push-prefs.test.ts reach it at all.
 * push.ts re-exports everything here, so callers are unaffected.
 */

/* ============================================================================
   What one device asked for
   ----------------------------------------------------------------------------
   Preferences live on the SUBSCRIPTION ROW, not on the account, because there
   is one shared `admin` login and everybody signs in as it — a per-account
   preference would be a single global switch wearing a personal label. Two
   people carrying two phones through the same login is the requirement this
   feature exists for, so the phone is the thing that holds the setting.
   ========================================================================== */

/** Mirrors prisma's AlertSeverity. Three values, and they are a total order. */
export type PushSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

/** One entry of GET /api/push/categories. Served, never duplicated here: a
 *  copy in the front end stops offering a category the day somebody adds one,
 *  and the drift is invisible from this side. */
export interface PushCategory {
  key: string;
  label: string;
  description: string;
  sources: string[];
}

/** The three columns a device carries about what it wants. */
export interface PushPrefs {
  minSeverity: PushSeverity;
  /** EMPTY MEANS EVERYTHING — see tickedCategories. */
  categories: string[];
  label: string | null;
}

/**
 * Which boxes to draw as ticked.
 *
 * AN EMPTY SELECTION MEANS EVERYTHING (src/alerts/categories.ts, wantsSource),
 * so drawing seven empty boxes over a device that receives all seven
 * categories would be an outright lie — and worse, the operator's natural fix
 * for it, ticking them all, is a change to a device that was already correct.
 * Empty renders as everything ticked instead. The half the checkboxes cannot
 * show — that empty also means categories invented after today — is said in
 * words by describeDelivery and on the panel.
 *
 * Selections the catalogue does not recognise are dropped rather than drawn,
 * which matches the PATCH route: it runs the same names through
 * parseCategories and drops what it does not know.
 */
export function tickedCategories(
  selected: readonly string[],
  all: readonly PushCategory[],
): string[] {
  if (selected.length === 0) return all.map((c) => c.key);
  return all.filter((c) => selected.includes(c.key)).map((c) => c.key);
}

export type CategoryChange =
  /** What to PATCH as `categories`. */
  | { ok: true; categories: string[] }
  /** The gesture has no honest representation; `reason` is for the screen. */
  | { ok: false; reason: string };

/**
 * Tick or untick one category and say what to send.
 *
 * TWO THINGS HERE THAT A CHECKBOX CANNOT DO ON ITS OWN.
 *
 * All ticked collapses back to [], so the device keeps picking up categories
 * that do not exist yet. A device pinned to today's seven names would silently
 * stop hearing the first new detector somebody writes — which for the phone
 * that asked for security is the failure that matters: not noise, but
 * confident silence.
 *
 * The last tick cannot be removed. [] is already spoken for, so "none" has no
 * representation at all, and sending it would hand the device EVERYTHING —
 * the exact opposite of the gesture. Refused with a reason rather than
 * silently swallowed or silently inverted.
 */
export function toggleCategory(
  selected: readonly string[],
  all: readonly PushCategory[],
  key: string,
): CategoryChange {
  const next = new Set(tickedCategories(selected, all));
  if (next.has(key)) next.delete(key);
  else next.add(key);

  if (next.size === 0) {
    return {
      ok: false,
      reason:
        'Leave at least one category ticked. A device with none selected receives everything, not nothing — to silence this device, revoke it or turn notifications off on it.',
    };
  }
  // Catalogue order, so a saved list reads the same way the boxes do.
  const chosen = all.filter((c) => next.has(c.key)).map((c) => c.key);
  /*
    An empty catalogue must not produce []. With `all = []` the collapse below
    reads `0 === 0` and returns [], which means EVERYTHING — the exact inverse
    of the tick that was just made. Unreachable through the current UI, because
    the checkboxes are rendered from `catalogue.map` and there is nothing to
    click when the catalogue is empty; reachable the moment anyone adds a
    "select all" control or a keyboard handler outside that map. This function
    is the one place the invariant is supposed to be defended, so it defends it
    rather than relying on its callers.
  */
  if (all.length === 0) {
    return { ok: false, reason: 'Categories have not loaded yet — try again in a moment.' };
  }
  return { ok: true, categories: chosen.length === all.length ? [] : chosen };
}

/**
 * One sentence saying what this device will actually receive.
 *
 * THIS IS THE LINE THE FEATURE EXISTS FOR. The controls above it are three
 * separate settings; this is the only place that says what they add up to, and
 * it is what somebody reads to confirm a colleague's phone is set to security
 * only before relying on it for a month.
 */
export function describeDelivery(
  prefs: Pick<PushPrefs, 'minSeverity' | 'categories'>,
  all: readonly PushCategory[],
): string {
  const severity =
    prefs.minSeverity === 'CRITICAL'
      ? 'CRITICAL only'
      : prefs.minSeverity === 'WARNING'
        ? 'WARNING and CRITICAL'
        : 'every severity';

  const chosen = all.filter((c) => prefs.categories.includes(c.key));
  let categories: string;
  if (prefs.categories.length === 0) {
    categories = 'every category (including ones added later)';
  } else if (all.length > 0 && chosen.length === all.length) {
    // Reachable even though the UI collapses "all ticked" to []: a row written
    // by an older client, or by hand against the API. It is NOT the same
    // promise as [] — it freezes the device on today's list — so it must not
    // read as though it were.
    categories = `all ${all.length} categories as they stand today, and nothing added later`;
  } else {
    // Falls back to the raw keys when the catalogue has not loaded, which is
    // uglier than the labels and still true.
    const names = chosen.length > 0 ? chosen.map((c) => c.label) : [...prefs.categories];
    categories = `${joinList(names)} only`;
  }

  return `Receives ${severity}, ${categories}.`;
}

function joinList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
